import { useMemo } from 'react';
import type { CoinbaseEngUsersAccountManagementApiApiProductRoleDefinition } from '@cbhq/identity-api-hooks/account-management/types';

import { useOrganization } from '../../../contexts/OrganizationContext';

import type { MemberRole, MemberRoleMap } from './constants';
import { PRODUCT_DISPLAY_NAMES } from './useRoles';

/**
 * Transforms a `ProductRoleDefinition[]` (the same shape returned by
 * admin-entities filteredRoles) into a roleMap keyed by full role ID.
 */
export function buildRolesFromProducts(
  products: CoinbaseEngUsersAccountManagementApiApiProductRoleDefinition[] | undefined,
): Record<MemberRole, MemberRoleMap> {
  const roleMap: Record<MemberRole, MemberRoleMap> = {};

  if (products) {
    products.forEach((product) => {
      if (!product.id || !product.roles) {
        return;
      }

      product.roles.forEach((role) => {
        if (!role.id || !product.id) {
          return;
        }
        const fullRoleId: MemberRole = `${product.id}/legal_entity:${role.id}`;
        if (roleMap[fullRoleId]) {
          return;
        }

        const productLabel = PRODUCT_DISPLAY_NAMES[product.id] ?? product.name ?? product.id;
        const name = role.name ?? role.id;

        roleMap[fullRoleId] = {
          value: fullRoleId,
          label: `${productLabel}: ${name}`,
          name,
          description: role.description || '',
          productKey: product.id,
        };
      });
    });
  }

  return roleMap;
}

export type UseRolesV2Return = {
  roleMap: Record<MemberRole, MemberRoleMap>;
  /** Union of all role IDs across every legal entity — use to distinguish supported vs hidden roles. */
  supportedRoles: MemberRole[];
  rolesByProductPerEntity: Record<string, Record<string, MemberRoleMap[]>>;
};

/**
 * V2 roles hook that derives available roles from admin-entities' per-entity
 * `filteredRoles` instead of the org-wide GetRoleDefinitions endpoint.
 * Use this in V2 tray flows (InviteTray/EditTray).
 */
export function useRolesV2(): UseRolesV2Return {
  const { selectedOrganization } = useOrganization();
  const legalEntities = selectedOrganization?.legalEntities;

  return useMemo(() => {
    const rolesByProductPerEntity: Record<string, Record<string, MemberRoleMap[]>> = {};
    const mergedRoleMap: Record<MemberRole, MemberRoleMap> = {};
    const mergedSupportedRoles: MemberRole[] = [];

    if (legalEntities) {
      for (const le of legalEntities) {
        if (le.filteredRoles) {
          const roleMap = buildRolesFromProducts(le.filteredRoles);

          const leRolesByProduct: Record<string, MemberRoleMap[]> = {};
          for (const option of Object.values(roleMap)) {
            (leRolesByProduct[option.productKey] ??= []).push(option);
          }
          rolesByProductPerEntity[le.legalEntityId] = leRolesByProduct;

          // Merge role maps for supportedRoles (union across entities).
          // First-occurrence-wins, mirroring V1's skip-if-already-present behavior.
          for (const [key, value] of Object.entries(roleMap)) {
            if (!mergedRoleMap[key]) {
              mergedRoleMap[key] = value;
              mergedSupportedRoles.push(key);
            }
          }
        } else {
          rolesByProductPerEntity[le.legalEntityId] = {};
        }
      }
    }

    return {
      roleMap: mergedRoleMap,
      supportedRoles: mergedSupportedRoles,
      rolesByProductPerEntity,
    };
  }, [legalEntities]);
}
